import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { FaChartBar, FaCheckCircle, FaEdit, FaEye, FaPauseCircle, FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api";
import Can from "../../components/Can";
import "./KraKpiConfigurationListPage.css";

// Task spec §3/§4: the KRA/KPI configuration catalog, manageable through the
// application instead of raw SQL. Same list+modal shape as
// pages/admins/TaskTypeManagementPage.jsx -- one page, no separate
// create/edit route. KRA/KPI/rating-rule/evidence-mapping/employee-assignment
// management lives in KraKpiConfigurationDetailPage.jsx's tabs (task spec
// §2: "Do not create unnecessary sidebar pages if tabs make the workflow
// cleaner").
const MODULE = "kra_kpi_management";

function emptyForm() {
  return { name: "", code: "", description: "", performance_year_start_month: 4 };
}

export default function KraKpiConfigurationListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/kra-kpi/configurations", { params: { search: search || undefined, pageSize: 100 } });
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch configurations.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchRows, 250);
    return () => clearTimeout(timer);
  }, [fetchRows]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowModal(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name, code: row.code, description: row.description || "", performance_year_start_month: row.performance_year_start_month });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/kra-kpi/configurations/${editing.id}`, form);
        toast.success("Configuration updated.");
      } else {
        await api.post("/admin/kra-kpi/configurations", form);
        toast.success("Configuration created.");
      }
      setShowModal(false);
      fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      await api.patch(`/admin/kra-kpi/configurations/${row.id}/status`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Configuration deactivated." : "Configuration activated.");
      fetchRows();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "KRA_KPI_CONFIG_WEIGHTAGE_INVALID") {
        const first = data?.details?.issues?.[0]?.message;
        toast.error(first ? `Cannot activate: ${first}` : "Cannot activate: weightage validation failed.");
      } else {
        toast.error(data?.message || "Failed to update status.");
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/kra-kpi/configurations/${deleteTarget.id}`);
      toast.success("Configuration deleted.");
      setDeleteTarget(null);
      fetchRows();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "KRA_KPI_CONFIG_IN_USE") {
        toast.error("This configuration has employee assignments and/or historical evidence and cannot be permanently deleted. Deactivate it instead.");
      } else {
        toast.error(data?.message || "Failed to delete configuration.");
      }
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="unit-page kkc-page">
      <div className="unit-header kkc-heading-box">
        <div className="kkc-heading-content">
          <div className="kkc-heading-icon">
            <FaChartBar aria-hidden="true" />
          </div>
          <div>
            <h2 className="mb-0">KRA / KPI Configurations</h2>
            <p className="mb-0">Performance-review frameworks assignable to employees.</p>
          </div>
        </div>
        <div className="unit-header-actions">
          <Can moduleKey={MODULE} actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openCreate}>
              <FaPlus aria-hidden="true" /> Create Configuration
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-card kkc-card">
        <div className="unit-toolbar kkc-toolbar">
          <InputGroup className="unit-search kkc-search">
            <InputGroup.Text aria-hidden="true">
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search by name or code..."
              aria-label="Search configurations by name or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>

        <div className="unit-table-wrapper">
          <Table responsive hover className="unit-table kkc-table">
            <colgroup>
              <col style={{ width: "24%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Configuration Name</th>
                <th>Performance Period</th>
                <th className="text-center">Total KRAs</th>
                <th className="text-center">Total KPIs</th>
                <th className="text-center">Assigned Employees</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
              ) : rows.length ? (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="kkc-name-cell">
                      <div className="fw-semibold">{row.name}</div>
                      <small className="text-muted kkc-code">{row.code}</small>
                    </td>
                    <td className="kkc-period-cell">{monthName(row.performance_year_start_month)} – {monthName(((row.performance_year_start_month + 10) % 12) + 1)}</td>
                    <td className="text-center">{row.kra_count}</td>
                    <td className="text-center">{row.kpi_count}</td>
                    <td className="text-center">{row.assigned_employee_count}</td>
                    <td>
                      <Badge className={`unit-status${row.is_active ? "" : " unit-status-inactive"}`}>
                        {row.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="kkc-date-cell">{row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}</td>
                    <td>
                      <div className="unit-actions kkc-actions justify-content-center">
                        <Can moduleKey={MODULE} actionKey="view">
                          <Button
                            className="unit-icon-btn kkc-action-view"
                            title="View Configuration"
                            aria-label={`View ${row.name || "configuration"}`}
                            onClick={() => navigate(`/admin/kra-kpi-configurations/${row.id}`)}
                          >
                            <FaEye aria-hidden="true" />
                          </Button>
                        </Can>
                        <Can moduleKey={MODULE} actionKey="edit">
                          <Button
                            className="unit-icon-btn unit-edit kkc-action-edit"
                            title="Edit Configuration"
                            aria-label={`Edit ${row.name || "configuration"}`}
                            onClick={() => openEdit(row)}
                          >
                            <FaEdit aria-hidden="true" />
                          </Button>
                        </Can>
                        <Can moduleKey={MODULE} actionKey="activate">
                          <Button
                            className={`unit-icon-btn kkc-action-toggle${row.is_active ? " kkc-action-toggle--deactivate" : " kkc-action-toggle--activate"}`}
                            title={row.is_active ? "Deactivate Configuration" : "Activate Configuration"}
                            aria-label={`${row.is_active ? "Deactivate" : "Activate"} ${row.name || "configuration"}`}
                            onClick={() => handleToggleStatus(row)}
                          >
                            {row.is_active ? <FaPauseCircle aria-hidden="true" /> : <FaCheckCircle aria-hidden="true" />}
                          </Button>
                        </Can>
                        <Can moduleKey={MODULE} actionKey="delete">
                          <Button
                            className="unit-icon-btn unit-delete kkc-action-delete"
                            title="Delete Configuration"
                            aria-label={`Delete ${row.name || "configuration"}`}
                            onClick={() => setDeleteTarget(row)}
                          >
                            <FaTrash aria-hidden="true" />
                          </Button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={8} className="text-center text-muted py-4">No configurations found.</td></tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing <strong>{rows.length}</strong> of <strong>{rows.length}</strong> configurations
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>{editing ? "Edit Configuration" : "Create Configuration"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Configuration Name</Form.Label>
            <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Developer — Standard KRA/KPI Framework" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Code</Form.Label>
            <Form.Control value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Auto-generated from name if left blank" disabled={!!editing} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Performance Year Start Month</Form.Label>
            <Form.Select value={form.performance_year_start_month} onChange={(e) => setForm({ ...form, performance_year_start_month: Number(e.target.value) })}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{monthName(m)}</option>
              ))}
            </Form.Select>
            <Form.Text muted>The current setup uses April (cycle: April → March). Not hardcoded — any month may start a configuration's own cycle.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete Configuration</Modal.Title></Modal.Header>
        <Modal.Body>
          Are you sure you want to permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          If it has any employee assignments or historical evidence, deletion will be blocked automatically — deactivate it instead.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? <Spinner animation="border" size="sm" /> : "Delete"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function monthName(m) {
  const names = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return names[m] || "—";
}
